import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { DeviceMobile, CheckCircle, Download, ShareNetwork, PlusSquare, DotsThreeVertical } from "@phosphor-icons/react";
import { isStandalone, isIOS, isIOSSafari, isAndroid, canPromptInstall, promptInstall, subscribeInstall } from "@/lib/install";
import { toast } from "sonner";

/**
 * Settings card that lets a user install BKflow as a home-screen app at any
 * time. Mirrors the discovery banner (InstallAppPrompt) but always visible.
 *
 * - Chrome / Edge (Android + desktop): triggers the native install prompt.
 * - iOS Safari: expands inline instructions (Share → "Sur l'écran d'accueil").
 * - Already installed: shows a green "Installée" state.
 */
export default function InstallAppCard() {
  const [installable, setInstallable] = useState(canPromptInstall());
  const [showSteps, setShowSteps] = useState(false);

  useEffect(() => {
    const unsub = subscribeInstall(() => setInstallable(canPromptInstall()));
    return unsub;
  }, []);

  const installed = isStandalone();

  const onInstall = async () => {
    if (installable) {
      const outcome = await promptInstall();
      if (outcome === "accepted") {
        toast.success("BKflow installée sur votre écran d'accueil");
      }
      return;
    }
    // iOS Safari (and fallbacks) → expand the manual steps
    setShowSteps((v) => !v);
  };

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3" data-testid="install-app-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#005CFF]/20 border border-[#005CFF]/30 flex items-center justify-center shrink-0">
          <DeviceMobile size={18} weight="duotone" className="text-[#7CB2FF]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold flex items-center gap-2">
            Installer l&apos;application
            {installed && <CheckCircle size={14} weight="fill" className="text-emerald-400" />}
          </div>
          <div className="text-[11px] text-white/50 leading-tight mt-0.5">
            {installed
              ? "BKflow est installée sur cet appareil"
              : "Ajoutez BKflow à votre écran d'accueil pour un accès rapide, plein écran, sans passer par le navigateur."}
          </div>
        </div>
      </div>

      {!installed && (
        <>
          <Button
            onClick={onInstall}
            className="w-full h-10 bg-[#005CFF] hover:bg-[#0047CC] rounded-xl text-sm gap-2"
            data-testid="install-app-btn"
          >
            <Download size={14} weight="bold" />
            {installable ? "Installer maintenant" : isIOS() ? (showSteps ? "Masquer les étapes" : "Voir les étapes iOS") : "Voir les étapes"}
          </Button>

          {showSteps && (
            <div className="rounded-xl bg-black/30 border border-white/5 p-3 space-y-3 animate-in fade-in slide-in-from-top-2">
              {isIOS() ? (
                isIOSSafari() ? <IOSSafariSteps /> : <IOSNonSafariSteps />
              ) : isAndroid() ? (
                <AndroidSteps />
              ) : (
                <DesktopSteps />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Step({ n, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-6 h-6 rounded-full bg-[#005CFF]/25 border border-[#005CFF]/40 flex items-center justify-center shrink-0 text-[#7CB2FF] text-[10px] font-bold">{n}</div>
      <div className="flex-1 text-xs text-white/80 leading-relaxed pt-0.5">{children}</div>
    </div>
  );
}

function Chip({ icon, children }) {
  return (
    <span className="inline-flex items-center gap-1 mx-0.5 px-1.5 py-0.5 rounded-md bg-white/10 border border-white/15 text-[10px] font-semibold align-middle">
      {icon}
      {children}
    </span>
  );
}

function IOSSafariSteps() {
  return (
    <>
      <Step n={1}>Appuyez sur <Chip icon={<ShareNetwork size={12} weight="bold" />}>Partager</Chip> en bas de Safari.</Step>
      <Step n={2}>Choisissez <Chip icon={<PlusSquare size={12} weight="bold" />}>Sur l&apos;écran d&apos;accueil</Chip>.</Step>
      <Step n={3}>Confirmez avec <b className="text-[#7CB2FF]">Ajouter</b>. L&apos;icône BKflow apparaît sur votre écran d&apos;accueil.</Step>
    </>
  );
}

function IOSNonSafariSteps() {
  return (
    <>
      <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-[10px]">
        L&apos;installation ne fonctionne <b>que dans Safari</b> sur iOS.
      </div>
      <Step n={1}>Copiez l&apos;adresse de cette page.</Step>
      <Step n={2}>Ouvrez <b>Safari</b>, collez et rechargez.</Step>
      <Step n={3}>Partager → <b>Sur l&apos;écran d&apos;accueil</b>.</Step>
    </>
  );
}

function AndroidSteps() {
  return (
    <>
      <Step n={1}>Ouvrez le menu <Chip icon={<DotsThreeVertical size={12} weight="bold" />}>Menu</Chip> en haut à droite de Chrome.</Step>
      <Step n={2}>Choisissez <b className="text-[#7CB2FF]">Installer l&apos;application</b> (ou <b>Ajouter à l&apos;écran d&apos;accueil</b>).</Step>
      <Step n={3}>Confirmez. L&apos;icône BKflow apparaît sur votre écran d&apos;accueil.</Step>
    </>
  );
}

function DesktopSteps() {
  return (
    <>
      <Step n={1}>Cliquez sur <Chip icon={<Download size={12} weight="bold" />}>Installer</Chip> dans la barre d&apos;adresse de Chrome / Edge.</Step>
      <Step n={2}>Confirmez pour créer un raccourci dans votre menu Démarrer / Applications.</Step>
    </>
  );
}
